import ky, { HTTPError, type KyInstance } from "ky";
import { useAuthStore } from "./auth";

/**
 * Cliente HTTP para la API de ByteISP.
 *
 * Características:
 *   - Lee el `accessToken` del store Zustand y lo añade como Bearer
 *   - Si responde 401, cierra sesión y manda al /login
 *   - Formatea errores a `ApiError` con código + mensaje (coincide con el
 *     schema del backend: `{error: {code, message}}`)
 */

export type ApiError = {
  status: number;
  code: string;
  message: string;
  details?: unknown;
};

async function toApiError(err: unknown): Promise<ApiError> {
  if (err instanceof HTTPError) {
    let body: { error?: { code?: string; message?: string }; detail?: unknown } = {};
    try {
      body = await err.response.clone().json();
    } catch {
      /* body no-JSON */
    }
    return {
      status: err.response.status,
      code: body.error?.code ?? `http_${err.response.status}`,
      message:
        body.error?.message ??
        (typeof body.detail === "string" ? body.detail : "") ??
        err.message,
      details: body.detail,
    };
  }
  return {
    status: 0,
    code: "network_error",
    message: err instanceof Error ? err.message : "Error de red",
  };
}

export const api: KyInstance = ky.create({
  prefixUrl: "",
  timeout: 15_000,
  retry: { limit: 0 },
  hooks: {
    beforeRequest: [
      (request) => {
        const token = useAuthStore.getState().accessToken;
        if (token) request.headers.set("Authorization", `Bearer ${token}`);
      },
    ],
    afterResponse: [
      async (_request, _options, response) => {
        if (response.status === 401) {
          useAuthStore.getState().logout();
          // Only redirect if we're not already on the login page
          if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
            window.location.href = "/login";
          }
        }
        return response;
      },
    ],
    beforeError: [
      async (error) => {
        // Dejamos que el error se propague pero guardamos la versión formateada
        const apiErr = await toApiError(error);
        (error as HTTPError & { apiError?: ApiError }).apiError = apiErr;
        return error;
      },
    ],
  },
});

/** Wrapper helper: ejecuta un request y devuelve JSON o lanza ApiError.
 *
 * `timeout` (ms) sobreescribe el timeout default (15s) — útil para endpoints
 * que tardan en propósito (auditoría sync Toku con check_huerfanas hace 1 GET
 * por segundo a Toku, puede llegar a 3-4 min).
 */
export async function apiCall<T>(
  method: "get" | "post" | "put" | "patch" | "delete",
  url: string,
  options?: {
    json?: unknown;
    searchParams?: Record<string, string | number | boolean>;
    timeout?: number;
  }
): Promise<T> {
  try {
    return await api[method](url, {
      json: options?.json,
      searchParams: options?.searchParams,
      ...(options?.timeout !== undefined ? { timeout: options.timeout } : {}),
    }).json<T>();
  } catch (err) {
    throw await toApiError(err);
  }
}

/** Utilidad específica: hace login y devuelve el response crudo. */
export type LoginResponse =
  | { token_type: "bearer"; access_token: string; expires_in_minutes: number }
  | { token_type: "password_change_required"; token: string; message: string }
  | { token_type: "tfa_challenge"; token: string; message: string };

export async function login(
  email: string,
  password: string,
  tfaCode?: string,
): Promise<LoginResponse> {
  return apiCall<LoginResponse>("post", "/auth/login", {
    json: { email, password, ...(tfaCode ? { tfa_code: tfaCode } : {}) },
  });
}

export async function changePassword(
  changeToken: string,
  current: string,
  next: string
): Promise<void> {
  try {
    await ky.post("/auth/change-password", {
      headers: { Authorization: `Bearer ${changeToken}` },
      json: {
        current_password: current,
        new_password: next,
        confirm_password: next,
      },
    });
  } catch (err) {
    throw await toApiError(err);
  }
}

export async function logout(): Promise<void> {
  try {
    await api.post("/auth/logout").catch(() => undefined);
  } finally {
    useAuthStore.getState().logout();
  }
}
